import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import {
  getSchoolTrialApplications,
  approveSchoolTrialApplication,
  rejectSchoolTrialApplication,
} from '../../api/adminService';

const STATUS_TABS = [
  { key: 'pending', label: '대기 중' },
  { key: 'approved', label: '승인됨' },
  { key: 'rejected', label: '반려됨' },
  { key: 'all', label: '전체' },
];

const REJECT_REASONS = [
  { value: 'missing_info', label: '학교 확인 어려움 (정보 부족)' },
  { value: 'suspicious', label: '허위/사칭 의심' },
  { value: 'duplicate', label: '중복 신청' },
  { value: 'other', label: '기타 (직접 입력)' },
];

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const isSchoolEmail = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return ['.sch.kr', '.go.kr', '.ac.kr'].some((d) => lower.includes(d));
};

const SchoolTrialReviewPage = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);

  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectCategory, setRejectCategory] = useState('missing_info');
  const [rejectDetail, setRejectDetail] = useState('');
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  const fetchItems = useCallback(async (statusFilter) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await getSchoolTrialApplications(statusFilter, 100);
      setItems(res.items || []);
    } catch (err) {
      setError(err.message || '신청 목록을 불러오지 못했어요.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const [p, a, r] = await Promise.all([
        getSchoolTrialApplications('pending', 200),
        getSchoolTrialApplications('approved', 200),
        getSchoolTrialApplications('rejected', 200),
      ]);
      setCounts({
        pending: p.count || 0,
        approved: a.count || 0,
        rejected: r.count || 0,
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchItems(status);
      fetchCounts();
    }
  }, [user, loading, status, fetchItems, fetchCounts, navigate]);

  const handleApprove = async (item) => {
    if (!window.confirm(
      `${item.school_name} (${item.teacher_count}명) 신청을 승인하시겠어요?\n\n` +
      `학교 단위 30일 PRO 쿠폰 1개가 발급되고,\n` +
      `${item.email} 으로 안내 메일과 ${item.phone} 으로 알림톡이 자동 발송됩니다.`
    )) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await approveSchoolTrialApplication(item.id);
      alert(
        `승인 완료!\n\n` +
        `쿠폰 코드: ${res.coupon_code}\n` +
        `발급 인원: ${res.coupon_quantity}계정\n\n` +
        `안내 메일과 알림톡이 발송되었습니다.`
      );
      setSelected(null);
      await fetchItems(status);
      await fetchCounts();
    } catch (err) {
      alert(`승인 실패: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (item) => {
    if (rejectCategory === 'other' && !rejectDetail.trim()) {
      alert('반려 사유를 입력해주세요.');
      return;
    }
    if (!window.confirm(`${item.school_name} 신청을 반려하시겠어요?\n사유: ${rejectDetail || REJECT_REASONS.find(r => r.value === rejectCategory)?.label}`)) {
      return;
    }
    setActionLoading(true);
    try {
      await rejectSchoolTrialApplication(item.id, rejectCategory, rejectDetail);
      alert('반려 처리되었습니다. 안내 메일이 발송되었어요.');
      setSelected(null);
      setRejectMode(false);
      setRejectDetail('');
      setRejectCategory('missing_info');
      await fetchItems(status);
      await fetchCounts();
    } catch (err) {
      alert(`반려 실패: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>불러오는 중...</div>;

  return (
    <div style={styles.container}>
      <Helmet>
        <title>학교 무료체험 검수 | 왓퀴즈 어드민</title>
      </Helmet>

      <div style={styles.header}>
        <h1 style={styles.title}>🏫 학교 30일 무료체험 검수</h1>
        <p style={styles.subtitle}>학교 대표가 신청한 30일 무료체험을 검토하고 학교 단위 쿠폰을 일괄 발급합니다.</p>
      </div>

      {/* 공식 검수 가이드 */}
      <div style={styles.guideBox}>
        <h3 style={styles.guideTitle}>📋 공식 검수 절차 (5단계)</h3>
        <p style={styles.guideIntro}>
          허위/사칭 신청을 차단하고 실제 학교 도입을 돕기 위한 공식 절차입니다. 모든 항목을 확인 후 승인 처리해주세요.
        </p>
        <ol style={styles.guideList}>
          <li>
            <strong>학교명 실존 확인</strong> — 학교알리미(schoolinfo.go.kr)에서 학교명+지역 검색 → 실존 여부 + 주소 일치 확인
            (모달의 "학교알리미 검색" 버튼 사용)
          </li>
          <li>
            <strong>이메일 도메인 확인</strong> — <code>@*.sch.kr</code> / <code>@*.go.kr</code> / <code>@*.ac.kr</code> 우선.
            네이버·구글 등 일반 이메일은 ⚠️ 표시되며 추가 검증 필수
          </li>
          <li>
            <strong>휴대폰 직접 통화 (최우선)</strong> — 입력된 번호로 직접 전화하여 학교 소속·직책·신청 의도 확인.
            전화 안 받으면 학교 대표번호로 콜백 후 "○○○ 선생님 계신가요?" 확인
          </li>
          <li>
            <strong>인원 수 적정성</strong> — 학교알리미 학생 수·교직원 수 대비 신청 인원이 합리적인지
            (예: 작은 분교에 100명 신청 → ⚠️)
          </li>
          <li>
            <strong>신청 사유 적절성</strong> — 사유가 구체적이고 학교 운영과 관련 있는지.
            모달 "신청 상세" 박스에 자동으로 🔁 중복 감지 표시되면 더 면밀히 확인
          </li>
        </ol>
        <p style={styles.guideFooter}>
          승인 시 <strong>학교 단위 쿠폰 1개(SCHOOL30-XXXXXX, 인원 수 = max_uses, 30일)</strong>가 자동 생성되고
          담당자에게 메일·알림톡(또는 SMS)로 즉시 안내됩니다. 반려 시 사유와 함께 안내 메일이 발송됩니다.
        </p>
      </div>

      <div style={styles.tabs}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatus(tab.key)}
            style={{
              ...styles.tab,
              ...(status === tab.key ? styles.tabActive : {}),
            }}
          >
            {tab.label}
            {tab.key !== 'all' && counts[tab.key] != null && (
              <span style={styles.tabBadge}>{counts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {isLoading ? (
        <div style={styles.loading}>불러오는 중...</div>
      ) : items.length === 0 ? (
        <div style={styles.empty}>표시할 신청이 없어요.</div>
      ) : (
        <div style={styles.list}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                ...styles.card,
                ...(selected?.id === item.id ? styles.cardActive : {}),
              }}
              onClick={() => {
                setSelected(item);
                setRejectMode(false);
              }}
            >
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.cardTitle}>{item.school_name}</h3>
                  <p style={styles.cardMeta}>
                    {item.school_type} · {item.school_region} · {item.teacher_count}명
                  </p>
                </div>
                <div style={styles.cardBadges}>
                  {isSchoolEmail(item.email) ? (
                    <span style={{ ...styles.badge, ...styles.badgeOk }}>✅ 학교 이메일</span>
                  ) : (
                    <span style={{ ...styles.badge, ...styles.badgeWarn }}>⚠️ 일반 이메일</span>
                  )}
                  <span style={styles.cardDate}>{formatDate(item.created_at)}</span>
                </div>
              </div>
              <div style={styles.cardBody}>
                <p style={styles.cardLine}>
                  <strong>{item.contact_name}</strong> ({item.contact_role})
                </p>
                <p style={styles.cardLine}>📧 {item.email} · 📞 {item.phone}</p>
                {item.reason && <p style={styles.cardReason}>사유: {item.reason}</p>}
                {item.status === 'approved' && item.coupon_code && (
                  <p style={styles.cardCoupon}>
                    🎫 쿠폰: <code>{item.coupon_code}</code> ({item.coupon_quantity}계정)
                  </p>
                )}
                {item.status === 'rejected' && item.reject_reason && (
                  <p style={styles.cardReject}>❌ 반려 사유: {item.reject_reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && selected.status === 'pending' && (
        <div style={styles.modal} onClick={() => { setSelected(null); setRejectMode(false); }}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{selected.school_name}</h2>
            <div style={styles.modalDetails}>
              <DetailRow label="학교 종류" value={selected.school_type} />
              <DetailRow label="지역" value={selected.school_region} />
              <DetailRow label="담당자" value={`${selected.contact_name} (${selected.contact_role})`} />
              <DetailRow label="이메일" value={selected.email}
                badge={isSchoolEmail(selected.email) ? '✅ 학교' : '⚠️ 일반'} />
              <DetailRow label="휴대폰" value={selected.phone} />
              <DetailRow label="예상 인원" value={`${selected.teacher_count}명`} />
              {selected.reason && <DetailRow label="도입 사유" value={selected.reason} />}
              <DetailRow label="신청 시각" value={formatDate(selected.created_at)} />
              {selected.utm_source && (
                <DetailRow label="UTM"
                  value={`${selected.utm_source} / ${selected.utm_medium || '-'} / ${selected.utm_campaign || '-'}`} />
              )}
            </div>

            <div style={styles.modalChecklistTitle}>✅ 검수 체크리스트 (확인 후 승인)</div>
            <ul style={styles.modalChecklist}>
              <li>① 학교알리미에서 학교명 실존·주소 일치 확인</li>
              <li>② 이메일 도메인 확인 ({isSchoolEmail(selected.email) ? '✅ 학교 도메인' : '⚠️ 일반 도메인 - 추가 검증 필요'})</li>
              <li>③ <strong>휴대폰({selected.phone})으로 직접 통화하여 학교 소속 확인 (필수)</strong></li>
              <li>④ 신청 인원({selected.teacher_count}명)이 학교 규모 대비 합리적인지</li>
              <li>⑤ 신청 사유 적절성 확인</li>
            </ul>

            <div style={styles.modalLinksTitle}>🔍 외부 검증 도구</div>
            <div style={styles.modalLinks}>
              <a
                href={`https://www.schoolinfo.go.kr/Common/cm_search.do?searchSchulNm=${encodeURIComponent(selected.school_name)}`}
                target="_blank" rel="noreferrer" style={styles.link}
              >학교알리미</a>
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(selected.school_name + ' ' + selected.school_region)}`}
                target="_blank" rel="noreferrer" style={styles.link}
              >Google</a>
              <a
                href={`https://map.naver.com/p/search/${encodeURIComponent(selected.school_name)}`}
                target="_blank" rel="noreferrer" style={styles.link}
              >네이버 지도</a>
              <a
                href={`tel:${selected.phone.replace(/-/g, '')}`}
                style={{ ...styles.link, background: '#fef3c7', color: '#92400e' }}
              >📞 직접 통화</a>
            </div>

            {!rejectMode ? (
              <div style={styles.modalActions}>
                <button
                  onClick={() => setRejectMode(true)}
                  style={{ ...styles.btn, ...styles.btnReject }}
                  disabled={actionLoading}
                >반려</button>
                <button
                  onClick={() => handleApprove(selected)}
                  style={{ ...styles.btn, ...styles.btnApprove }}
                  disabled={actionLoading}
                >{actionLoading ? '처리 중...' : '승인 + 쿠폰 발급'}</button>
              </div>
            ) : (
              <div style={styles.rejectForm}>
                <label style={styles.rejectLabel}>반려 사유 분류</label>
                <select
                  value={rejectCategory}
                  onChange={(e) => setRejectCategory(e.target.value)}
                  style={styles.select}
                >
                  {REJECT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <label style={styles.rejectLabel}>상세 사유 (선택, 메일에 그대로 표시)</label>
                <textarea
                  value={rejectDetail}
                  onChange={(e) => setRejectDetail(e.target.value)}
                  placeholder="예: 학교 사이트에서 해당 학교 확인이 어려워 추가 자료가 필요합니다."
                  rows={3}
                  style={styles.textarea}
                />
                <div style={styles.modalActions}>
                  <button onClick={() => setRejectMode(false)}
                    style={{ ...styles.btn, ...styles.btnCancel }}
                    disabled={actionLoading}
                  >취소</button>
                  <button onClick={() => handleReject(selected)}
                    style={{ ...styles.btn, ...styles.btnReject }}
                    disabled={actionLoading}
                  >{actionLoading ? '처리 중...' : '반려 + 안내 메일 발송'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, badge }) => (
  <div style={styles.detailRow}>
    <span style={styles.detailLabel}>{label}</span>
    <span style={styles.detailValue}>
      {value}
      {badge && <span style={{ ...styles.badge, ...styles.badgeInline }}>{badge}</span>}
    </span>
  </div>
);

const styles = {
  container: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '32px 24px 80px',
    fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#191f28',
  },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, margin: '0 0 6px' },
  subtitle: { fontSize: 14, color: '#6b7684', margin: 0 },
  tabs: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  tab: {
    padding: '10px 16px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    color: '#4e5968',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'inherit',
  },
  tabActive: { background: '#3182f6', color: '#fff', borderColor: '#3182f6' },
  tabBadge: {
    background: 'rgba(255,255,255,0.25)',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
  },
  error: { padding: 14, background: '#fef2f2', color: '#991b1b', borderRadius: 10, marginBottom: 16 },
  empty: { padding: 60, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 12 },
  loading: { padding: 60, textAlign: 'center', color: '#6b7684' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 20,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  cardActive: { borderColor: '#3182f6', boxShadow: '0 0 0 3px rgba(49,130,246,0.1)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: '0 0 4px' },
  cardMeta: { fontSize: 13, color: '#6b7684', margin: 0 },
  cardBadges: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 },
  cardDate: { fontSize: 12, color: '#9ca3af' },
  cardBody: { borderTop: '1px solid #f2f4f6', paddingTop: 10 },
  cardLine: { fontSize: 13, color: '#4e5968', margin: '2px 0' },
  cardReason: { fontSize: 13, color: '#6b7684', margin: '6px 0 0', fontStyle: 'italic' },
  cardCoupon: { fontSize: 13, color: '#10b981', margin: '6px 0 0', fontWeight: 600 },
  cardReject: { fontSize: 13, color: '#ef4444', margin: '6px 0 0' },
  badge: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12 },
  badgeOk: { background: '#d1fae5', color: '#065f46' },
  badgeWarn: { background: '#fef3c7', color: '#92400e' },
  badgeInline: { marginLeft: 8 },
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modalBox: {
    background: '#fff', borderRadius: 16, padding: 28,
    maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
  },
  modalTitle: { fontSize: 20, fontWeight: 700, margin: '0 0 20px' },
  modalDetails: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f2f4f6' },
  detailLabel: { fontSize: 13, color: '#6b7684' },
  detailValue: { fontSize: 14, color: '#191f28', fontWeight: 500, textAlign: 'right' },
  modalChecklistTitle: { fontSize: 13, fontWeight: 700, color: '#1f2937', marginTop: 16, marginBottom: 8 },
  modalChecklist: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 10,
    padding: '12px 14px 12px 28px',
    margin: '0 0 16px',
    fontSize: 13,
    color: '#166534',
    lineHeight: 1.7,
  },
  modalLinksTitle: { fontSize: 13, fontWeight: 700, color: '#1f2937', marginTop: 12, marginBottom: 8 },
  modalLinks: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  link: { fontSize: 13, color: '#3182f6', textDecoration: 'none', padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontWeight: 600 },
  guideBox: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 12,
    padding: '20px 24px',
    marginBottom: 24,
  },
  guideTitle: { fontSize: 15, fontWeight: 700, color: '#92400e', margin: '0 0 8px' },
  guideIntro: { fontSize: 13, color: '#78350f', margin: '0 0 12px', lineHeight: 1.6 },
  guideList: {
    margin: 0,
    paddingLeft: 20,
    fontSize: 13,
    color: '#451a03',
    lineHeight: 1.8,
  },
  guideFooter: {
    fontSize: 12,
    color: '#78350f',
    marginTop: 12,
    marginBottom: 0,
    paddingTop: 12,
    borderTop: '1px solid #fde68a',
    lineHeight: 1.6,
  },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  btn: {
    padding: '12px 24px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 14,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnApprove: { background: '#3182f6', color: '#fff' },
  btnReject: { background: '#ef4444', color: '#fff' },
  btnCancel: { background: '#f3f4f6', color: '#4e5968' },
  rejectForm: { background: '#f9fafb', padding: 16, borderRadius: 10, marginTop: 8 },
  rejectLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '12px 0 6px' },
  select: { width: '100%', padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' },
  textarea: { width: '100%', padding: 10, border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
};

export default SchoolTrialReviewPage;
